<ng-template let-editableElement="editableElement" let-isOnTop="isOnTop" #buttonbar>
	<stages-buttons-bottom [isOnTop]="isOnTop" [removeTopPadding]="true">
		<stages-button
			[buttonType]="'button'"
			[buttonCSS]="'button sm cancel'"
			[title]="'cancel'"
			(clicked)="cancel()"
		></stages-button>
		<stages-button
			[buttonType]="'submit'"
			[buttonCSS]="'button sm save'"
			[title]="'save'"
			[disabled]="isSaveInProgress"
			[showLoadingSpin]="isSaveInProgress"
			(clicked)="save(editableElement)"
		>
		</stages-button>
	</stages-buttons-bottom>
</ng-template>

<ng-container *ngIf="workspaceView$ | async as workspaceView">
	<ng-container *ngIf="data$ | async as data">
		<article class="edit-element" *ngIf="editableElement$ | async as editableElement">
			<header class="page-header">
				<ul class="breadcrumb">
					<li>
						<a class="back" (click)="cancel()">
							<i class="ico ico-arrow-back"></i><span class="label" translate="back"></span>
						</a>
					</li>
				</ul>
				<h1 translate="process.element.edit.title"></h1>
				<stages-breadcrumb [path]="getBreadcrumbPath(data.view)" [links]="true"></stages-breadcrumb>
			</header>

			<article class="card">
				<ng-container
					*ngTemplateOutlet="buttonbar; context: { editableElement: editableElement, isOnTop: true }"
				></ng-container>
				<form class="form" [formGroup]="formGroup" *ngIf="editableElement.element" #formElement>
					<div class="form-group" *ngIf="!editableElement.element.isIndex">
						<div class="input-container md required">
							<label class="input-label" for="name" translate="process.element.edit.name"></label>
							<input
								id="name"
								name="name"
								type="text"
								class="text fullsize"
								tabindex="1"
								formControlName="name"
								stagesAutofocus
								(change)="setErrorsOnForm(getFormControl('name'))"
							/>
							<div class="info">
								<div
									*ngIf="
										getFormControl('name').errors && (getFormControl('name').dirty || getFormControl('name').touched)
									"
								>
									<ng-container *ngFor="let error of getControlErrors('name')" [ngSwitch]="error">
										<div *ngSwitchCase="'required'" class="error" [translate]="'input.required'" role="alert"></div>
										<div
											*ngSwitchCase="'maxlength'"
											class="error"
											[translate]="'input.maxLength'"
											[translateParams]="{ length: '255' }"
											role="alert"
										></div>
										<div
											*ngSwitchCase="'pattern'"
											class="error"
											[translate]="'process.element.edit.name.pattern'"
										></div>
									</ng-container>
								</div>
								<div class="maxlength">{{ getMaxLengthAsString(getFormControl("name"), "255") }}</div>
							</div>
						</div>

						<div class="input-container sm" *ngIf="editableElement.shortnameEnabled">
							<label class="input-label" for="shortname" translate="process.element.edit.abbreviation"></label>
							<input
								id="shortname"
								name="shortname"
								type="text"
								class="text fullsize"
								tabindex="2"
								formControlName="shortname"
								(change)="setErrorsOnForm(getFormControl('shortname'))"
							/>
							<div class="info">
								<div
									*ngIf="
										getFormControl('shortname').errors &&
										(getFormControl('shortname').dirty || getFormControl('shortname').touched)
									"
								>
									<ng-container *ngFor="let error of getControlErrors('shortname')" [ngSwitch]="error">
										<div
											*ngSwitchCase="'maxlength'"
											class="error"
											[translate]="'input.maxLength'"
											[translateParams]="{ length: '32' }"
											role="alert"
										></div>
										<div
											*ngSwitchCase="'pattern'"
											class="error"
											[translate]="'process.element.edit.abbreviation.pattern'"
										></div>
									</ng-container>
								</div>
								<div class="maxlength">{{ getMaxLengthAsString(getFormControl("shortname"), "32") }}</div>
							</div>
						</div>
					</div>

					<div class="form-group" *ngIf="!editableElement.element.isIndex && editableElement.validSubtypes.length">
						<div class="input-container md">
							<label class="input-label" for="type" translate="process.element.edit.type"></label>
							<select id="type" formControlName="subtypeIdent" tabindex="3">
								<option
									*ngFor="let subtype of editableElement.validSubtypes"
									value="{{ subtype.subtypeIdent }}"
									translate="{{ subtype.name!.keys[0] }}"
								></option>
							</select>
							<div class="info"></div>
						</div>

						<div class="input-container blind sm"></div>
					</div>

					<div class="input-container">
						<label class="input-label" for="edt1" translate="process.element.edit.description"></label>
						<stages-description-editor
							[editorId]="'elementDescriptionEditor'"
							[modifiable]="true"
							[unsafe]="false"
							[description]="editableElement.element.displayDescription"
							[processTypeIdent]="data.view.processView.processType"
							[pluginRegistry]="pluginRegistry"
							[beanType]="data.view.processView.type.ident"
							[beanId]="data.view.processView.id"
							[beanIdentity]="data.view.processView.identity"
							[workspaceId]="workspaceView.currentWorkspace.id"
							[pv]="workspaceView.currentWorkspace.viewedProcess!.versionIdentifier"
						>
						</stages-description-editor>
						<div class="info"></div>
					</div>

					<div class="input-container" *ngIf="!editableElement.element.isIndex">
						<legend class="input-label" translate="security.level"></legend>
						<ng-container
							*ngFor="
								let i of [].constructor(editableElement.maximumAvailableSecurityLevel + 1);
								let levelNumber = index
							"
						>
							<div class="radio" *ngIf="levelNumber > 0">
								<input
									[tabindex]="levelNumber + 4"
									[attr.aria-label]="'security.level.' + levelNumber | translate"
									[title]="'security.level.' + levelNumber | translate"
									[id]="'security-level-' + levelNumber"
									type="radio"
									name="securityLevel"
									[value]="levelNumber"
									formControlName="securityLevel"
								/>
								<label [for]="'security-level-' + levelNumber" [translate]="'security.level.' + levelNumber"></label>
							</div>
						</ng-container>
						<div class="info"></div>
					</div>

					<stages-attribute-edit
						[attributeTypes]="editableElement.attributeTypes"
						[attributes]="editableElement.element.attributes"
						[form]="formGroup"
						[richtextInputContext]="getContext(data, workspaceView)"
					>
					</stages-attribute-edit>

					<div class="info"></div>
					<ng-container
						*ngTemplateOutlet="buttonbar; context: { editableElement: editableElement, isOnTop: false }"
					></ng-container>
				</form>
			</article>
		</article>
	</ng-container>
</ng-container>
